import { type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { usePersistedWalletAddress } from 'dibs-crypto-utils/exports/usePersistedWalletAddress.ts';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection.ts';
import { useChainNames } from 'dibs-crypto-utils/exports/useChainNames.ts';
import { browser } from 'dibs-client-check';
import { type useWalletConnectionEnsured_viewer$key } from './__generated__/useWalletConnectionEnsured_viewer.graphql.ts';

function isUnsupportedMetamaskBrowser(isMobile: boolean): boolean {
    return (
        typeof window !== 'undefined' &&
        ((!isMobile && browser.safari()) || (isMobile && !window.ethereum))
    );
}

export const useWalletConnectionEnsured = ({
    viewer: viewerRef,
    isMobile,
}: {
    viewer: useWalletConnectionEnsured_viewer$key;
    isMobile: boolean;
}): {
    hasAddressMismatch: boolean;
    isUnsupportedChain: boolean;
    isAddressPersisted: boolean;
    isWalletConnected: boolean;
    isMetamaskUnsupported: boolean;
    persistedAddress: string;
    userId: string;
    connectedNetwork: string | ReactNode;
    requiredNetworks: string | ReactNode;
} => {
    const viewer = useFragment(
        graphql`
            fragment useWalletConnectionEnsured_viewer on Viewer {
                ...usePersistedWalletAddress_viewer
                    @arguments(fetchUser: $fetchUser, userId: $userId)
            }
        `,
        viewerRef
    );
    const { persistedAddress, userId } = usePersistedWalletAddress({ viewer });
    const { unsupportedChain, account: connectedAddress, unsupportedWCChain } = useWeb3Connection();

    const addressMismatch =
        connectedAddress &&
        persistedAddress &&
        connectedAddress.toLocaleLowerCase() !== persistedAddress.toLocaleLowerCase();

    const isUnsupportedChain = unsupportedChain || unsupportedWCChain;

    const { connectedNetwork, requiredNetworks } = useChainNames();

    return {
        hasAddressMismatch: !!addressMismatch,
        isUnsupportedChain,
        isAddressPersisted: !!persistedAddress,
        isWalletConnected: !!connectedAddress,
        isMetamaskUnsupported: isUnsupportedMetamaskBrowser(isMobile),
        persistedAddress,
        userId,
        connectedNetwork,
        requiredNetworks,
    };
};
